import { PagePathService } from '@/src/services/PagePathService'
import { useState, useEffect } from 'react'
import { useLoginUser } from '../../states/LoginUser'
import { Props } from './Layout'
import { useLatestJunksQuery, useMyNfTsQuery } from './query.generated'
import { useProcessingContactsContext } from '../../states/ProcessingContactsContext'
import { useUpdateUserIconMutation } from '../../dialogs/IconEditDialog/query.generated'
import { ApolloError } from '@apollo/client'
import { DISABLED_AMS } from '@/src/assets/constants'
import { AKV__factory } from '@victgame/akiverse-ft-contracts'
import { MetamaskService } from '@/src/services/MetamaskService'
import { AKVWei } from '@/src/services/AKVService'
import { useMetamaskWalletAddress } from '../../states/MetamaskWalletAddress'

export const useProps = (): Props => {
  const loginUser = useLoginUser().loginUser
  const userId = loginUser?.id ?? ''
  const accountUserName = loginUser?.name ?? ''
  const terasBalance = loginUser?.terasBalance ?? 0
  const akvBalance = loginUser?.akvBalance
  const iconType = loginUser?.iconType
  const iconSubCategory = loginUser?.iconSubCategory
  const frameSubCategory = loginUser?.frameSubCategory
  const titleSubCategory = loginUser?.titleSubCategory
  const address = loginUser?.walletAddress
  const reload = loginUser?.reload
  const [state, setState] = useState<Props['state']>('Default')
  const [nfts, setNfts] = useState<Props['nfts']>([])
  const [convertingJunkId, setConvertingJunkId] = useState<Props['convertingJunkId']>()
  const [userName, setUserName] = useState<string>(accountUserName)
  const [akvWalletBalance, setAkvWalletBalance] = useState<AKVWei | undefined>(undefined)
  const [visibleDepositAkvDialog, setVisibleDepositAkvDialog] = useState(false)
  const [visibleWithdrawAkvDialog, setVisibleWithdrawAkvDialog] = useState(false)
  const [akvState, setAkvState] = useState<Props['akvState']>('Approve')
  const currentMetamskWalletAddress = useMetamaskWalletAddress().walletAddress

  const [updateUser] = useUpdateUserIconMutation()
  const { data, refetch } = useMyNfTsQuery({
    variables: {
      where: { userId: { equals: userId } },
      arcadeMachinesWhere2: { userId: { equals: userId }, destroyedAt: { equals: null } },
      arcadePartsWhere2: { userId: { equals: userId }, destroyedAt: { equals: null } },
    },
  })
  const junksQueryResult = useLatestJunksQuery({ variables: { userId } })
  const junks: Props['junks'] =
    junksQueryResult.data?.junks.map((d) => ({
      id: d.id,
      amount: d.amount,
      imageUrl: d.metadata.image,
      name: d.metadata.name,
      rarity: d.metadata.rarity,
      junksPerPart: d.metadata.junksPerPart,
      disabled: DISABLED_AMS.includes(d.subCategory),
    })) ?? []
  const {
    depositingGameCenters,
    withdrawingGameCenters,
    depositingArcadeMachines,
    withdrawingArcadeMachines,
    depositingArcadeParts,
    withdrawingArcadeParts,
    depositingAKV,
    withdrawingAKV,
  } = useProcessingContactsContext()

  // useProcessingContactsContextは定期実行されているので各NFTの状態が変わったことを検知してrefechするための処理
  const isDepositingOrWithdrawingGameCenters = depositingGameCenters.length === withdrawingGameCenters.length
  const isDepositingOrWithdrawingArcadeMachines =
    depositingArcadeMachines.length === withdrawingArcadeMachines.length
  const isDepositingOrWithdrawingArcadeParts = depositingArcadeParts.length === withdrawingArcadeParts.length
  const isDepositingOrWithdrawingAKV = depositingAKV.length === withdrawingAKV.length

  useEffect(() => {
    refetch()
  }, [
    isDepositingOrWithdrawingGameCenters,
    isDepositingOrWithdrawingArcadeMachines,
    isDepositingOrWithdrawingArcadeParts,
    refetch,
  ])

  useEffect(() => {
    reload && reload()
    // 保有しているAKVを取得
    currentMetamskWalletAddress &&
      currentMetamskWalletAddress === address &&
      new AKV__factory()
        .attach(process.env.NEXT_PUBLIC_AKV_CONTRACT_ADDRESS)
        .connect(MetamaskService.getSigner())
        .balanceOf(address!)
        .then((balanceOf) => setAkvWalletBalance(new AKVWei(balanceOf.toString())))
  }, [isDepositingOrWithdrawingAKV, reload, address, currentMetamskWalletAddress])

  useEffect(() => {
    setNfts(
      [
        ...(data?.gameCenters.map<Props['nfts'][number]>((gc) => ({
          id: gc.id,
          imageUrl: gc.metadata.image,
          name: gc.metadata.name,
          type: 'GameCenter',
          linkUrl: PagePathService.gameCenterManagerShowCaseDetail(gc.id),
          state: gc.state,
        })) ?? []),
        ...(data?.arcadeMachines.map<Props['nfts'][number]>((am) => ({
          id: am.id,
          imageUrl: am.metadata.image || '',
          name: am.metadata.name,
          type: 'AracdeMachine',
          linkUrl: PagePathService.arcadeMachineManagerShowCaseDetail(am.id),
          state: am.state,
        })) ?? []),
        ...(data?.arcadeParts.map<Props['nfts'][number]>((ap) => ({
          id: ap.id,
          imageUrl: ap.metadata.image,
          name: ap.metadata.name,
          type: 'ArcadeParts',
          linkUrl: PagePathService.myArcadeParts(),
          state: ap.state,
          rarity: ap.metadata.rarity,
        })) ?? []),
      ]
        .sort(() => Math.random() - 0.5)
        .slice(0, 21),
    )
  }, [data])

  const onClickRefresh = () => {
    loginUser?.reload()
    currentMetamskWalletAddress &&
      currentMetamskWalletAddress === address &&
      new AKV__factory()
        .attach(process.env.NEXT_PUBLIC_AKV_CONTRACT_ADDRESS)
        .connect(MetamaskService.getSigner())
        .balanceOf(address!)
        .then((balanceOf) => setAkvWalletBalance(new AKVWei(balanceOf.toString())))
  }

  return {
    state,
    onClickEditIcon: () => setState('Edit_Icon'),
    onSaveIcon: () => {
      loginUser?.reload()
      setState('Default')
    },
    onClickCancel: () => setState('Default'),
    userName: accountUserName,
    userWalletAddress: address || '',
    onClickEditName: () => {
      setState('Edit_Name')
      setUserName(accountUserName)
    },
    onChangeUserName: (s) => setUserName(s),
    onClickNameSave: async () => {
      setState('Loading')
      try {
        const res = await updateUser({
          variables: {
            input: { name: userName, iconType, iconSubCategory, frameSubCategory, titleSubCategory },
          },
        })
        if (res.data) {
          setState('Default')
          setUserName('')
          loginUser?.reload()
        } else {
          alert('Error')
          console.error(res.errors)
        }
      } catch (e) {
        const error = e as ApolloError
        setState('Edit_Name')
        alert('Error: ' + error.message)
        console.error(e)
      }
    },
    inputUserName: userName,
    nfts,
    junks,
    terasBalance: String(terasBalance),
    onClickRefreshBalances: onClickRefresh,
    onClickRefreshAkv: onClickRefresh,
    akvWalletBalance: akvWalletBalance?.toAKV() ?? '',
    akvInAkiverseBalance: akvBalance?.toAKV() ?? '',
    akvState,
    visibleDepositAkvDialog,
    onClickDepositAkv: async () => {
      const allowance = await new AKV__factory()
        .attach(process.env.NEXT_PUBLIC_AKV_CONTRACT_ADDRESS)
        .connect(MetamaskService.getSigner())
        .allowance(address!, process.env.NEXT_PUBLIC_AKIVERSE_LOCKER_ADDRESS)
      setAkvState(allowance.lt(akvWalletBalance?.akvWei ?? 0) ? 'Approve' : 'Deposit')
      setVisibleDepositAkvDialog(true)
    },
    visibleWithdrawAkvDialog,
    onClickWithdrawAkv: () => setVisibleWithdrawAkvDialog(true),
    onClickAKVComplete: async () => {
      await loginUser?.reload()

      const balanceOf = await new AKV__factory()
        .attach(process.env.NEXT_PUBLIC_AKV_CONTRACT_ADDRESS)
        .connect(MetamaskService.getSigner())
        .balanceOf(address!)
      setAkvWalletBalance(new AKVWei(balanceOf.toString()))

      setVisibleDepositAkvDialog(false)
      setVisibleWithdrawAkvDialog(false)
    },
    onClickClose: () => {
      setVisibleDepositAkvDialog(false)
      setVisibleWithdrawAkvDialog(false)
    },
    onClickJunkConvertDialogClose: () => setConvertingJunkId(undefined),
    onCompletedJunkConvert: () => junksQueryResult.refetch(),
    convertingJunkId,
    onClickConvert: setConvertingJunkId,
  }
}
